import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import IconButton from './IconButton';
import ExerciseNoteModal from './ExerciseNoteModal';
import { useProfile } from '../context/ProfileContext';
import { fetchExerciseHistory } from '../firestoreUtils';
import { fetchExerciseNotesForDays } from '../services/exerciseNotesService';
import { groupByDay } from '../utils/groupLogs';
import { formatLogAmount } from '../utils/formatLogAmount';
import './comic.css';

const MAX_HISTORY_RESULTS = 50;

/**
 * Read-only-except-for-notes popup showing one exercise's past logged sets
 * (newest first, grouped by calendar day), opened via a small "i" info icon
 * wherever an exercise is shown in the app (ActiveMission's live cards,
 * RoutinePicker's builder, Manage My Exercises, Quick Start, and the shared
 * ExerciseSelectorModal's own search/browse list). Scoped to the active
 * Hero, same as every other logs query in this app. Each day group also
 * carries a note icon (green when that day already has a note) opening
 * ExerciseNoteModal for that exercise/day -- one of the three surfaces
 * notes are viewable/editable from, alongside ActiveMission (where they're
 * written) and "Previously..." (WorkoutLog.jsx).
 */
const ExerciseHistoryModal = ({ exerciseId, exerciseName, userId, tabKey, onClose }) => {
  const { activeProfile } = useProfile();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState([]);
  const [notesByDay, setNotesByDay] = useState({});
  // Which day's note popup is open: { dayKey, dayLabel } or null.
  const [noteDay, setNoteDay] = useState(null);

  useEffect(() => {
    if (!userId || !activeProfile || !exerciseId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    fetchExerciseHistory(userId, activeProfile.id, exerciseId, MAX_HISTORY_RESULTS).then(async (entries) => {
      if (cancelled) return;
      const dayGroups = groupByDay(entries);
      setGroups(dayGroups);
      setLoading(false);
      const notes = await fetchExerciseNotesForDays(userId, activeProfile.id, exerciseId, dayGroups.map((g) => g.key));
      if (!cancelled) setNotesByDay(notes);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, activeProfile, exerciseId]);

  return (
    <>
    <Modal title={`${exerciseName} History`} tabKey={tabKey} onClose={onClose}>
      {loading ? (
        <p className="comic-empty">Loading...</p>
      ) : groups.length === 0 ? (
        <p className="comic-empty">No history yet for this exercise.</p>
      ) : (
        groups.map((group) => (
          <div key={group.key} style={{ marginBottom: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
              <h4 className="comic-narration-date" style={{ margin: 0 }}>{group.header}</h4>
              <IconButton
                icon="note"
                onClick={() => setNoteDay({ dayKey: group.key, dayLabel: group.header })}
                color={notesByDay[group.key] ? "#00a274" : "#232526"}
                label={`${notesByDay[group.key] ? "Edit" : "Add"} note for ${group.header}`}
              />
            </div>
            {group.entries.map((log) => (
              <div key={log.id} className="stamp-row">
                <span className="stamp-row__text">
                  {formatLogAmount(log)}{log.routineName ? ` · ${log.routineName}` : ""}
                </span>
                {log.isPR && <span className="pr-spark">🔥</span>}
              </div>
            ))}
          </div>
        ))
      )}
    </Modal>

    {noteDay && (
      <ExerciseNoteModal
        exerciseId={exerciseId}
        exerciseName={exerciseName}
        dayKey={noteDay.dayKey}
        dayLabel={noteDay.dayLabel}
        userId={userId}
        tabKey={tabKey}
        onClose={() => setNoteDay(null)}
        onSaved={(text) => setNotesByDay((prev) => ({ ...prev, [noteDay.dayKey]: text }))}
      />
    )}
    </>
  );
};

export default ExerciseHistoryModal;
